import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../shared'

export const Root = styled(GlassPanel)({
  top: 58,
  right: 12,
  bottom: 78,
  width: 264,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

export const Head = styled(Box)({ padding: '13px 14px 10px', borderBottom: '1px solid var(--ds-night-border)' })
export const Kind = styled(Box)({ ...monoLabelSx, fontSize: 7.5, color: '#be9dfd' })
export const TitleInput = styled('input')({
  width: '100%',
  marginTop: 4,
  background: 'none',
  border: 'none',
  outline: 'none',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font)',
  fontSize: 14.5,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  padding: '2px 0',
  borderBottom: '1px solid transparent',
  '&:focus': { borderBottomColor: 'rgba(190,157,253,0.5)' },
})
export const Sub = styled(Box)({ ...monoLabelSx, marginTop: 2, fontSize: 8 })

export const Scroll = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  // Explicit, not just relying on the default: browsers compute `overflow-x`
  // to `auto` too once `overflow-y` isn't `visible`, so any stray content
  // overflow in here (see `RangeInput`'s `minWidth: 0` fix) would otherwise
  // grow its own horizontal scrollbar instead of wrapping/clipping.
  overflowX: 'hidden',
  padding: '4px 14px 16px',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(244,243,246,0.14) transparent',
})

export const Section = styled(Box)({ padding: '13px 0 3px', borderBottom: '1px solid rgba(244,243,246,0.06)', '&:last-of-type': { borderBottom: 'none' } })
export const SectionLabel = styled(Typography)({ ...monoLabelSx, fontSize: 7.5, marginBottom: 9 })

export const SwatchGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  // Padding so selected swatch rings (outer box-shadow) aren't clipped by Scroll.
  gap: 8,
  padding: 4,
  margin: '0 -4px 8px',
})

export const Note = styled(Typography)({ fontSize: 10.5, lineHeight: 1.55, color: 'var(--ds-night-ink-subtle)', margin: '-3px 0 12px' })

export const DangerLink = styled('span')({
  fontSize: 11.5,
  fontWeight: 600,
  color: '#f2795f',
  cursor: 'pointer',
  display: 'block',
  margin: '2px 0 12px',
  '&:hover': { textDecoration: 'underline' },
})

export const AccentLink = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 11.5,
  fontWeight: 600,
  color: '#01dafe',
  marginBottom: 12,
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
})

export const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
  fontSize: 12,
  color: 'var(--ds-night-ink-muted)',
})

export const FrameRow = styled(Box)({ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 4 })

export const FrameChip = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 42,
  height: 42,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#1a1922',
  border: `1px solid ${$on ? 'rgba(255,207,125,0.55)' : 'var(--ds-night-border)'}`,
  outline: $on ? '2px solid rgba(255,207,125,0.28)' : 'none',
  outlineOffset: 1,
  cursor: 'pointer',
  transition: 'border-color 140ms ease, outline-color 140ms ease',
  '&:hover': { borderColor: 'rgba(244,243,246,0.3)' },
}))

export const Switch = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  width: 30,
  height: 17,
  borderRadius: 10,
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  background: $on ? '#be9dfd' : 'rgba(244,243,246,0.14)',
  transition: 'background 260ms ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 2,
    left: 2,
    width: 13,
    height: 13,
    borderRadius: '50%',
    background: '#fff',
    transition: 'transform 260ms ease',
    transform: $on ? 'translateX(13px)' : 'translateX(0)',
  },
}))

/**
 * Last-slot custom color control — spectrum chip + PRO badge; opens a
 * `FinishColorPanel` sheet for whichever surface it's wired to (wall/floor/
 * ceiling in `RoomInspector`, mat in `PrintInspector`). Shared so every
 * "curated 4-ish stock tones + PRO extended palette" picker looks and
 * behaves identically instead of drifting per-surface.
 */
export const ProPickerBtn = styled('button', { shouldForwardProp: (p) => p !== '$selected' })<{ $selected?: boolean }>(
  ({ $selected }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    width: 24,
    height: 24,
    borderRadius: 8,
    cursor: 'pointer',
    flexShrink: 0,
    position: 'relative',
    background: 'conic-gradient(from 180deg, #fe6a6a, #ffcf7d, #7dffb3, #01dafe, #be9dfd, #fe6a6a)',
    boxShadow: $selected
      ? `inset 0 0 0 1px rgba(255,255,255,0.25), 0 0 0 2px #131219, 0 0 0 3.5px ${STUDIO_ACCENT.violet}`
      : 'inset 0 0 0 1px rgba(255,255,255,0.2)',
    transition: 'transform 140ms ease, box-shadow 140ms ease',
    '&:hover': { transform: 'scale(1.12)' },
  }),
)

export const ProPickerBadge = styled('span')({
  position: 'absolute',
  right: -5,
  top: -6,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 5.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#0a0912',
  background: STUDIO_ACCENT.gold,
  borderRadius: 3,
  padding: '1px 3px',
  lineHeight: 1.2,
  pointerEvents: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.45)',
})

/** True when `hex` isn't one of the curated stock swatches — i.e. a PRO custom color is active. */
export function isCustomTone(hex: string, palette: string[]) {
  return !palette.some((p) => p.toLowerCase() === hex.toLowerCase())
}
